import { useCallback, useRef, useState } from 'react';
import {
  useLatestSyncRuns,
  useSyncBusinessPartners,
  useSyncEmployees,
  useSyncItems,
  useSyncShippingTypes,
  useSyncSapOrders,
  useComputeItemKpis,
  useSyncRuns,
} from '@/api/admin/admin.hooks';
import type { SyncRunResult } from '@/api/admin/admin.types';
import { DsButton } from '@/modules/internal-v2/ds';
import { ContainedPage } from '../../shared/contained-page.component';
import { IfPermitted } from '../../shared/if-permitted.component';
import { PERMISSIONS } from '@/constants/permissions';

type SyncStatus = 'idle' | 'running' | 'success' | 'error';

interface SyncState {
  status: SyncStatus;
  elapsed: number | null;
  error: string | null;
  result: SyncRunResult | null;
}

function useSyncWithTimer(
  mutationHook: () => ReturnType<typeof useSyncBusinessPartners>,
) {
  const [state, setState] = useState<SyncState>({
    status: 'idle',
    elapsed: null,
    error: null,
    result: null,
  });
  const startTimeRef = useRef<number>(0);
  const mutation = mutationHook();

  const trigger = useCallback(() => {
    startTimeRef.current = Date.now();
    setState({ status: 'running', elapsed: null, error: null, result: null });
    mutation.mutate(undefined, {
      onSuccess: (data) => {
        setState({
          status: 'success',
          elapsed: Date.now() - startTimeRef.current,
          error: null,
          result: data,
        });
      },
      onError: (err) => {
        setState({
          status: 'error',
          elapsed: Date.now() - startTimeRef.current,
          error:
            (err.response?.data as { message?: string })?.message ||
            err.message ||
            'Unbekannter Fehler',
          result: null,
        });
      },
    });
  }, [mutation]);

  return { state, trigger };
}

const SYNC_TYPE_MAP: Record<string, string> = {
  'business-partners': 'businessPartners',
  employees: 'employees',
  items: 'items',
  'shipping-types': 'shippingTypes',
  'sap-orders': 'sapOrders',
  'item-kpis': 'itemKpis',
};

const SYNC_TYPE_LABELS: Record<string, string> = {
  items: 'Artikel',
  'business-partners': 'Geschäftspartner',
  employees: 'Mitarbeiter',
  'shipping-types': 'Versandarten',
  'sap-orders': 'SAP Bestellungen',
  'item-kpis': 'KPI-Berechnung',
};

const SYNC_CONFIGS = [
  {
    key: 'businessPartners' as const,
    syncType: 'business-partners',
    title: 'Geschäftspartner',
    description: 'Geschäftspartner, Adressen und Kontaktpersonen aus SAP B1.',
    cronLabel: 'Alle 30 Min',
    hook: useSyncBusinessPartners,
  },
  {
    key: 'employees' as const,
    syncType: 'employees',
    title: 'Mitarbeiter',
    description: 'Mitarbeiter- und Verkäuferdaten aus SAP B1.',
    cronLabel: 'Alle 30 Min',
    hook: useSyncEmployees,
  },
  {
    key: 'items' as const,
    syncType: 'items',
    title: 'Artikel',
    description: 'Artikel, Preise, Warengruppen, Hersteller und MwSt.-Gruppen.',
    cronLabel: 'Alle 30 Min',
    hook: useSyncItems,
  },
  {
    key: 'shippingTypes' as const,
    syncType: 'shipping-types',
    title: 'Versandarten',
    description: 'Versandarten und Versandkosten pro Geschäftspartner.',
    cronLabel: 'Alle 30 Min',
    hook: useSyncShippingTypes,
  },
  {
    key: 'sapOrders' as const,
    syncType: 'sap-orders',
    title: 'SAP Bestellungen',
    description: 'Bestellhistorie für KPIs und Dashboards.',
    cronLabel: 'Alle 30 Min',
    hook: useSyncSapOrders,
  },
  {
    key: 'itemKpis' as const,
    syncType: 'item-kpis',
    title: 'KPI-Berechnung',
    description: 'Einkaufs-KPIs: Demand Score, Kundendurchdringung, Konzentration.',
    cronLabel: 'Täglich 02:15',
    hook: useComputeItemKpis,
  },
];

type SyncKey = (typeof SYNC_CONFIGS)[number]['key'];

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SyncTab() {
  const businessPartners = useSyncWithTimer(useSyncBusinessPartners);
  const employees = useSyncWithTimer(useSyncEmployees);
  const items = useSyncWithTimer(useSyncItems);
  const shippingTypes = useSyncWithTimer(useSyncShippingTypes);
  const sapOrders = useSyncWithTimer(useSyncSapOrders);
  const itemKpis = useSyncWithTimer(useComputeItemKpis);

  const syncs: Record<SyncKey, { state: SyncState; trigger: () => void }> = {
    businessPartners,
    employees,
    items,
    shippingTypes,
    sapOrders,
    itemKpis,
  };

  const { data: latestRuns } = useLatestSyncRuns();

  const latestByType = (latestRuns ?? []).reduce(
    (acc, run) => {
      const key = SYNC_TYPE_MAP[run.syncType];
      if (key) acc[key] = run;
      return acc;
    },
    {} as Record<string, SyncRunResult>,
  );

  const anyRunning = Object.values(syncs).some((s) => s.state.status === 'running');

  return (
    <ContainedPage
      contained
      toolbar={
        <IfPermitted permission={PERMISSIONS.SYNC_TRIGGER}>
          <div style={{ marginLeft: 'auto' }}>
            <DsButton
              variant="primary"
              size="small"
              disabled={anyRunning}
              onClick={() => Object.values(syncs).forEach((s) => s.trigger())}
            >
              ↻ Alle synchronisieren
            </DsButton>
          </div>
        </IfPermitted>
      }
    >
        <div className="admin-card-grid">
          {SYNC_CONFIGS.map((config) => {
            const s = syncs[config.key];
            const latest = latestByType[config.key];
            const displayResult = s.state.result || latest;

            return (
              <div key={config.key} className="admin-card">
                <div className="admin-card-header">
                  <span className="admin-card-title">{config.title}</span>
                  <span className="admin-card-badge">{config.cronLabel}</span>
                </div>
                <div className="admin-card-desc">{config.description}</div>

                {s.state.status === 'running' && (
                  <div className="admin-progress running">
                    <div className="admin-progress-fill" />
                  </div>
                )}

                {displayResult && displayResult.status !== 'running' && (
                  <div className="admin-results">
                    <span className="ds-pill pos">{displayResult.totalProcessed} verarbeitet</span>
                    {displayResult.totalSkipped > 0 && (
                      <span className="ds-pill warn">{displayResult.totalSkipped} übersprungen</span>
                    )}
                    {displayResult.totalDeleted > 0 && (
                      <span className="ds-pill muted">{displayResult.totalDeleted} gelöscht</span>
                    )}
                  </div>
                )}

                <div className="admin-card-footer">
                  <div className={`admin-status ${s.state.status}`}>
                    <span className="dot" />
                    <span className="label">
                      {s.state.status === 'idle' && 'Bereit'}
                      {s.state.status === 'running' && 'Synchronisierung…'}
                      {s.state.status === 'success' && 'Abgeschlossen'}
                      {s.state.status === 'error' && 'Fehlgeschlagen'}
                      {s.state.elapsed != null && ` (${formatDuration(s.state.elapsed)})`}
                    </span>
                  </div>
                  <IfPermitted permission={PERMISSIONS.SYNC_TRIGGER}>
                    <DsButton
                      size="small"
                      disabled={s.state.status === 'running'}
                      onClick={s.trigger}
                    >
                      Sync starten
                    </DsButton>
                  </IfPermitted>
                </div>

                {s.state.status === 'idle' && latest && (
                  <div className="admin-last-run">
                    Letzter Lauf: {formatDate(latest.startedAt)} ({latest.trigger === 'cron' ? 'Cron' : 'Manuell'}, {formatDuration(latest.durationMs)})
                  </div>
                )}

                {s.state.status === 'error' && s.state.error && (
                  <div className="admin-error-box">{s.state.error}</div>
                )}
              </div>
            );
          })}
        </div>

        <SyncHistory />
    </ContainedPage>
  );
}

function SyncHistory() {
  const { data } = useSyncRuns({ limit: 25 });
  const [selectedRun, setSelectedRun] = useState<SyncRunResult | null>(null);

  if (!data?.data?.length) return null;

  return (
    <>
      <div className="admin-section-label" style={{ marginTop: 8 }}>Sync-Verlauf</div>
      <table className="ds-table" style={{ fontSize: 'var(--fs-xs)' }}>
        <thead>
          <tr>
            <th>Typ</th>
            <th>Auslöser</th>
            <th>Status</th>
            <th>Gestartet</th>
            <th>Dauer</th>
            <th className="num">Abgerufen</th>
            <th className="num">Verarbeitet</th>
            <th className="num">Übersprungen</th>
            <th className="num">Gelöscht</th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((run) => (
            <tr key={run.id}>
              <td>{SYNC_TYPE_LABELS[run.syncType] ?? run.syncType}</td>
              <td>
                <span className="ds-pill muted">
                  {run.trigger === 'cron' ? 'Cron' : 'Manuell'}
                </span>
              </td>
              <td>
                <span
                  className={`ds-pill ${run.status === 'completed' ? 'pos' : run.status === 'failed' ? 'neg' : 'muted'}`}
                >
                  {run.status === 'completed' ? 'Erfolg' : run.status === 'failed' ? 'Fehler' : 'Läuft'}
                </span>
              </td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>
                {new Date(run.startedAt).toLocaleString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{formatDuration(run.durationMs)}</td>
              <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{run.totalFetched}</td>
              <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{run.totalProcessed}</td>
              <td className="num">
                {run.totalSkipped > 0 ? (
                  <button
                    className="ds-btn ghost small"
                    style={{ color: 'var(--warn)', fontFamily: 'var(--font-mono)' }}
                    onClick={() => setSelectedRun(run)}
                  >
                    {run.totalSkipped}
                  </button>
                ) : (
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>0</span>
                )}
              </td>
              <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{run.totalDeleted}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedRun && (
        <SkippedOverlay run={selectedRun} onClose={() => setSelectedRun(null)} />
      )}
    </>
  );
}

function SkippedOverlay({ run, onClose }: { run: SyncRunResult; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        style={{ flex: 1, background: 'rgba(20, 24, 36, 0.3)' }}
        onClick={onClose}
      />
      <div
        style={{
          width: 520,
          maxWidth: '100vw',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="bp-overlay-bar">
          <button className="bp-cmd-back" onClick={onClose}>← Schließen</button>
          <span className="bp-overlay-title">Übersprungene Datensätze</span>
        </div>
        <div style={{ padding: '10px 14px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          <span className="ds-pill muted">{SYNC_TYPE_LABELS[run.syncType] ?? run.syncType}</span>
          <span className="ds-pill muted">{formatDate(run.startedAt)}</span>
          <span className="ds-pill warn">{run.skippedRecords.length} übersprungen</span>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }} className="ds-scroll">
          <table className="ds-table" style={{ fontSize: 'var(--fs-xs)' }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Grund</th>
              </tr>
            </thead>
            <tbody>
              {run.skippedRecords.map((record, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{record.id}</td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{record.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
